import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Users, Settings, CreditCard, DollarSign, BookOpen, FileText, Brain,
  ArrowLeft, Loader2, Trash2, Plus, Minus, Save, Eye, RefreshCw,
  BarChart3, TrendingUp, Calendar, Download, Crown, Mail, Send, Check, Bell, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0); // 0-indexed
  const [usersLoading, setUsersLoading] = useState(false);
  const USERS_PAGE_SIZE = 50;
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [savingSettings, setSavingSettings] = useState(false);
  const isMountedRef = useRef(true);
  
  // LLM Usage Analytics state
  const [llmUsage, setLlmUsage] = useState(null);
  const [llmUsagePeriod, setLlmUsagePeriod] = useState("7d");
  const [loadingLlmUsage, setLoadingLlmUsage] = useState(false);
  
  // Announcement state
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementData, setAnnouncementData] = useState({
    subject: "",
    title: "",
    content: "",
    target_audience: "all"
  });
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [showClearSmtpDialog, setShowClearSmtpDialog] = useState(false);
  const [clearingSmtp, setClearingSmtp] = useState(false);
  // Push broadcast state
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [pushData, setPushData] = useState({
    title: "",
    body: "",
    url: "/dashboard",
    target_audience: "all",
  });
  const [sendingPush, setSendingPush] = useState(false);
  const [pushStats, setPushStats] = useState(null);
  // Stable random suffix generated ONCE per mount so browser can't recognize field names for autofill
  const smtpFieldSuffix = useRef(Math.random().toString(36).slice(2, 10)).current;

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!user?.is_admin) {
      navigate("/dashboard");
      return;
    }
    fetchData();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [user]);

  const fetchData = async () => {
    // Don't fetch if component is unmounted or no token
    if (!isMountedRef.current || !token) return;
    
    try {
      const [statsRes, usersRes, settingsRes, analyticsRes, llmUsageRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders(token) }),
        axios.get(`${API}/admin/users?skip=${usersPage * USERS_PAGE_SIZE}&limit=${USERS_PAGE_SIZE}`, { headers: getAuthHeaders(token) }),
        axios.get(`${API}/admin/settings`, { headers: getAuthHeaders(token) }),
        axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders(token) }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/llm-usage?period=${llmUsagePeriod}`, { headers: getAuthHeaders(token) }).catch(() => ({ data: null }))
      ]);
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setUsersTotal(usersRes.data.total || 0);
      setSettings(settingsRes.data);
      setAnalytics(analyticsRes.data);
      setLlmUsage(llmUsageRes.data);
    } catch (error) {
      // Only show error if component is still mounted and it's not a logout scenario
      if (!isMountedRef.current) return;
      
      if (error.response?.status === 401) {
        // User logged out or token expired - don't show error
        return;
      } else if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchUsersPage = async (newPage) => {
    if (!isMountedRef.current || !token) return;
    setUsersLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/users?skip=${newPage * USERS_PAGE_SIZE}&limit=${USERS_PAGE_SIZE}`,
        { headers: getAuthHeaders(token) }
      );
      if (!isMountedRef.current) return;
      setUsers(response.data.users || []);
      setUsersTotal(response.data.total || 0);
      setUsersPage(newPage);
    } catch (error) {
      if (isMountedRef.current && error.response?.status !== 401) {
        toast.error("Failed to load users");
      }
    } finally {
      if (isMountedRef.current) setUsersLoading(false);
    }
  };


  const fetchLlmUsage = async (period) => {
    if (!isMountedRef.current || !token) return;
    
    setLoadingLlmUsage(true);
    try {
      const response = await axios.get(`${API}/admin/llm-usage?period=${period}`, {
        headers: getAuthHeaders(token)
      });
      if (isMountedRef.current) {
        setLlmUsage(response.data);
      }
    } catch (error) {
      if (isMountedRef.current && error.response?.status !== 401) {
        toast.error("Failed to load LLM usage data");
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingLlmUsage(false);
      }
    }
  };

  const handleLlmPeriodChange = (period) => {
    setLlmUsagePeriod(period);
    fetchLlmUsage(period);
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders(token)
      });
      setSelectedUser(response.data);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleUpdateCredits = async (userId) => {
    if (creditAmount === 0) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/credits`, 
        { credits: creditAmount },
        { headers: getAuthHeaders(token) }
      );
      toast.success(`Credits ${creditAmount > 0 ? 'added' : 'removed'}`);
      fetchData();
      setCreditAmount(0);
      if (selectedUser) {
        handleViewUser(userId);
      }
    } catch (error) {
      toast.error("Failed to update credits");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteUser.user_id}`, {
        headers: getAuthHeaders(token)
      });
      toast.success("User deleted");
      setDeleteUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleClearSmtp = async () => {
    setClearingSmtp(true);
    try {
      const clearedSettings = {
        ...settings,
        smtp_host: "",
        smtp_port: 587,
        smtp_user: "",
        smtp_password: "",
        smtp_from_email: "",
        smtp_from_name: "BookTest.pro",
        smtp_use_tls: true,
      };
      await axios.put(`${API}/admin/settings`, clearedSettings, {
        headers: getAuthHeaders(token)
      });
      setSettings(clearedSettings);
      toast.success("SMTP settings cleared. Enter new credentials and save.");
      setShowClearSmtpDialog(false);
    } catch (error) {
      toast.error("Failed to clear SMTP settings");
    } finally {
      setClearingSmtp(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: getAuthHeaders(token)
      });
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const updatePackage = (pkgId, field, value) => {
    setSettings(prev => ({
      ...prev,
      credit_packages: {
        ...prev.credit_packages,
        [pkgId]: {
          ...prev.credit_packages[pkgId],
          [field]: field === 'credits' ? parseInt(value) || 0 : parseFloat(value) || 0
        }
      }
    }));
  };

  const updateCost = (costId, value) => {
    setSettings(prev => ({
      ...prev,
      credit_costs: {
        ...prev.credit_costs,
        [costId]: parseInt(value) || 0
      }
    }));
  };

  const handleSendAnnouncement = async () => {
    if (!announcementData.subject || !announcementData.title || !announcementData.content) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSendingAnnouncement(true);
    try {
      const response = await axios.post(`${API}/admin/send-announcement`, announcementData, {
        headers: getAuthHeaders(token)
      });
      toast.success(`Announcement sent to ${response.data.sent_count} users!`);
      setShowAnnouncementDialog(false);
      setAnnouncementData({ subject: "", title: "", content: "", target_audience: "all" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send announcement");
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const openPushDialog = async () => {
    setShowPushDialog(true);
    try {
      const { data } = await axios.get(`${API}/admin/push/stats`, {
        headers: getAuthHeaders(token),
      });
      setPushStats(data);
    } catch (e) {
      setPushStats({ total_subscriptions: 0, configured: false });
    }
  };

  const handleSendPush = async () => {
    if (!pushData.title || !pushData.body) {
      toast.error("Title and message are required");
      return;
    }
    setSendingPush(true);
    try {
      const { data } = await axios.post(`${API}/admin/push/send`, pushData, {
        headers: getAuthHeaders(token),
      });
      toast.success(`Push sent: ${data.sent} delivered, ${data.failed} failed`);
      setShowPushDialog(false);
      setPushData({ title: "", body: "", url: "/dashboard", target_audience: "all" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send push");
    } finally {
      setSendingPush(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <DashboardHeader />

      <main className="container-main py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base text-slate-600">Manage users, settings, and pricing</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="default" 
              className="gap-1 sm:gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-xs sm:text-sm px-2 sm:px-4"
              onClick={() => setShowAnnouncementDialog(true)}
            >
              <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Send Announcement</span>
              <span className="sm:hidden">Announce</span>
            </Button>
            <Button
              variant="default"
              className="gap-1 sm:gap-2 bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm px-2 sm:px-4"
              onClick={openPushDialog}
              data-testid="admin-open-push-dialog-btn"
            >
              <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Send Push</span>
              <span className="sm:hidden">Push</span>
            </Button>
            <Button variant="outline" onClick={fetchData} className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4">
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button 
              variant="outline" 
              className="gap-1 sm:gap-2 text-red-600 border-red-200 hover:bg-red-50 text-xs sm:text-sm px-2 sm:px-4"
              onClick={async () => {
                if (window.confirm("This will delete all test users and their data (classrooms, tests, quizzes). Continue?")) {
                  try {
                    const response = await axios.delete(`${API}/admin/cleanup-test-data`, {
                      headers: getAuthHeaders(token)
                    });
                    toast.success(`Cleaned up ${response.data.deleted.users} test users and ${response.data.deleted.classrooms} classrooms`);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to clean test data");
                  }
                }
              }}
            >
              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Clean Test Data</span>
              <span className="sm:hidden">Clean</span>
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-[#4F46E5] mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">{stats?.total_users || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Users</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <BookOpen className="w-4 h-4 sm:w-6 sm:h-6 text-[#14B8A6] mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">{stats?.total_books || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Books</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-orange-500 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">{stats?.total_tests || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Tests</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <Brain className="w-4 h-4 sm:w-6 sm:h-6 text-purple-500 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">{stats?.total_quizzes || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Quizzes</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <CreditCard className="w-4 h-4 sm:w-6 sm:h-6 text-green-500 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">{stats?.total_payments || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Payments</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-2 sm:p-4 text-center">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-500 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold">${stats?.total_revenue || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Revenue</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm flex flex-wrap gap-1 h-auto">
            <TabsTrigger value="users" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Users</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Subscriptions</span>
              <span className="sm:hidden">Subs</span>
            </TabsTrigger>
            <TabsTrigger value="packages" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Packages & Costs</span>
              <span className="sm:hidden">Costs</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Payment Settings</span>
              <span className="sm:hidden">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Email Settings</span>
              <span className="sm:hidden">Email</span>
            </TabsTrigger>
            <TabsTrigger value="llm-usage" className="gap-1 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <Brain className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">LLM Costs</span>
              <span className="sm:hidden">LLM</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="card-base">
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  {usersTotal > 0
                    ? `${usersTotal} total user${usersTotal === 1 ? "" : "s"} · most recent first`
                    : "View and manage all users"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Name</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Email</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Role</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Credits</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Premium</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-500">Joined</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{u.name}</span>
                              {u.is_admin && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Admin</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-slate-600">{u.email}</td>
                          <td className="p-3">
                            <span className="capitalize">{u.role}</span>
                          </td>
                          <td className="p-3">
                            {u.email_verified !== false ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1 w-fit">
                                <Check className="w-3 h-3" />
                                Verified
                              </span>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Unverified</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/users/${u.user_id}/verify`, {}, {
                                        headers: getAuthHeaders(token)
                                      });
                                      toast.success("User verified!");
                                      fetchData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to verify user");
                                    }
                                  }}
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  Verify
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/users/${u.user_id}/resend-verification`, {}, {
                                        headers: getAuthHeaders(token)
                                      });
                                      toast.success("Verification email sent!");
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to send email");
                                    }
                                  }}
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  Resend
                                </Button>
                              </div>
                            )}
                          </td>
                          <td className="p-3 font-medium">{u.credits}</td>
                          <td className="p-3">
                            {u.role === 'teacher' ? (
                              <button
                                className={`relative w-10 h-5 rounded-full transition-colors ${
                                  u.is_premium ? 'bg-yellow-500' : 'bg-slate-300'
                                }`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/users/${u.user_id}/premium`, 
                                      { is_premium: !u.is_premium },
                                      { headers: getAuthHeaders(token) }
                                    );
                                    toast.success(`Premium ${!u.is_premium ? 'activated' : 'deactivated'}`);
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Failed to update premium");
                                  }
                                }}
                              >
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                  u.is_premium ? 'translate-x-5' : 'translate-x-0.5'
                                }`} />
                              </button>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="p-3 text-slate-500 text-sm">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewUser(u.user_id)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {!u.is_admin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => setDeleteUser(u)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Users Pagination */}
                {usersTotal > USERS_PAGE_SIZE && (
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 px-1" data-testid="users-pagination">
                    <p className="text-sm text-slate-600" data-testid="users-pagination-info">
                      Showing <strong>{usersPage * USERS_PAGE_SIZE + 1}</strong>–
                      <strong>{Math.min((usersPage + 1) * USERS_PAGE_SIZE, usersTotal)}</strong> of{" "}
                      <strong>{usersTotal}</strong> users
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsersPage(usersPage - 1)}
                        disabled={usersPage === 0 || usersLoading}
                        className="gap-1"
                        data-testid="users-pagination-prev"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600 px-2" data-testid="users-pagination-current">
                        Page {usersPage + 1} of {Math.max(1, Math.ceil(usersTotal / USERS_PAGE_SIZE))}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUsersPage(usersPage + 1)}
                        disabled={(usersPage + 1) * USERS_PAGE_SIZE >= usersTotal || usersLoading}
                        className="gap-1"
                        data-testid="users-pagination-next"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Growth Charts */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      User Growth
                    </CardTitle>
                    <CardDescription>New users over the last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics?.user_growth ? (
                      <div className="space-y-4">
                        <div className="flex items-end gap-1 h-32">
                          {analytics.user_growth.map((day, idx) => (
                            <div
                              key={idx}
                              className="flex-1 bg-[#4F46E5] rounded-t opacity-80 hover:opacity-100 transition-opacity"
                              style={{ height: `${Math.max(10, (day.count / Math.max(...analytics.user_growth.map(d => d.count))) * 100)}%` }}
                              title={`${day.date}: ${day.count} users`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>30 days ago</span>
                          <span>Today</span>
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center text-slate-400">
                        No data available yet
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-emerald-500" />
                      Revenue Trend
                    </CardTitle>
                    <CardDescription>Revenue over the last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analytics?.revenue_trend ? (
                      <div className="space-y-4">
                        <div className="flex items-end gap-1 h-32">
                          {analytics.revenue_trend.map((day, idx) => (
                            <div
                              key={idx}
                              className="flex-1 bg-emerald-500 rounded-t opacity-80 hover:opacity-100 transition-opacity"
                              style={{ height: `${Math.max(10, (day.amount / Math.max(...analytics.revenue_trend.map(d => d.amount), 1)) * 100)}%` }}
                              title={`${day.date}: $${day.amount.toFixed(2)}`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>30 days ago</span>
                          <span>Today</span>
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center text-slate-400">
                        No revenue data yet
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Activity Stats */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#4F46E5]" />
                    Activity Breakdown
                  </CardTitle>
                  <CardDescription>Content creation statistics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-white rounded-xl text-center">
                      <BookOpen className="w-8 h-8 text-[#4F46E5] mx-auto mb-2" />
                      <p className="text-2xl font-bold">{stats?.total_books || 0}</p>
                      <p className="text-sm text-slate-500">Books Analyzed</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-orange-50 to-white rounded-xl text-center">
                      <FileText className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{stats?.total_tests || 0}</p>
                      <p className="text-sm text-slate-500">Tests Created</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-white rounded-xl text-center">
                      <Brain className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{stats?.total_quizzes || 0}</p>
                      <p className="text-sm text-slate-500">Quizzes Created</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-teal-50 to-white rounded-xl text-center">
                      <Users className="w-8 h-8 text-[#14B8A6] mx-auto mb-2" />
                      <p className="text-2xl font-bold">{analytics?.active_classrooms || 0}</p>
                      <p className="text-sm text-slate-500">Active Classrooms</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Distribution */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle>User Roles Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.role_distribution ? (
                      <div className="space-y-3">
                        {Object.entries(analytics.role_distribution).map(([role, count]) => (
                          <div key={role} className="flex items-center gap-3">
                            <span className="w-20 text-sm capitalize">{role}</span>
                            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  role === 'teacher' ? 'bg-[#4F46E5]' :
                                  role === 'student' ? 'bg-[#14B8A6]' : 'bg-orange-500'
                                }`}
                                style={{ width: `${(count / (stats?.total_users || 1)) * 100}%` }}
                              />
                            </div>
                            <span className="w-12 text-sm text-right font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-slate-400 py-4">No data</div>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-base">
                  <CardHeader>
                    <CardTitle>Top Users by Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics?.top_users?.length > 0 ? (
                      <div className="space-y-2">
                        {analytics.top_users.slice(0, 5).map((u, idx) => (
                          <div key={u.user_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                              idx === 1 ? 'bg-slate-300 text-slate-700' :
                              idx === 2 ? 'bg-orange-300 text-orange-800' : 'bg-slate-100 text-slate-600'
                            }`}>{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{u.name}</p>
                              <p className="text-xs text-slate-500 truncate">{u.email}</p>
                            </div>
                            <span className="text-sm font-medium">{u.total_activity}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-slate-400 py-4">No activity data</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    Premium Subscription Settings
                  </CardTitle>
                  <CardDescription>Manage the premium teacher subscription tier</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                    <h4 className="font-semibold text-lg mb-4">Premium Teacher Plan</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Monthly Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={settings?.premium_price || 14.99}
                          onChange={(e) => setSettings(prev => ({ ...prev, premium_price: parseFloat(e.target.value) || 14.99 }))}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Classrooms</Label>
                        <Input
                          type="number"
                          value={settings?.premium_max_classrooms || 10}
                          onChange={(e) => setSettings(prev => ({ ...prev, premium_max_classrooms: parseInt(e.target.value) || 10 }))}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Students per Class</Label>
                        <Input
                          type="number"
                          value={settings?.premium_max_students || 50}
                          onChange={(e) => setSettings(prev => ({ ...prev, premium_max_students: parseInt(e.target.value) || 50 }))}
                          className="h-9 mt-1"
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white rounded-lg">
                      <Label className="text-xs text-slate-500">Premium Benefits</Label>
                      <ul className="mt-2 space-y-1 text-sm">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                          Unlimited tests and quizzes
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                          Create and manage classrooms
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                          Share tests with students
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                          Track student scores and progress
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle>Active Subscribers</CardTitle>
                  <CardDescription>Teachers with premium subscriptions</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.premium_subscribers?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 text-sm font-medium text-slate-500">Teacher</th>
                            <th className="text-left p-3 text-sm font-medium text-slate-500">Classrooms</th>
                            <th className="text-left p-3 text-sm font-medium text-slate-500">Students</th>
                            <th className="text-left p-3 text-sm font-medium text-slate-500">Since</th>
                            <th className="text-left p-3 text-sm font-medium text-slate-500">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.premium_subscribers.map((sub) => (
                            <tr key={sub.user_id} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{sub.name}</p>
                                  <p className="text-xs text-slate-500">{sub.email}</p>
                                </div>
                              </td>
                              <td className="p-3">{sub.classrooms_count || 0}</td>
                              <td className="p-3">{sub.total_students || 0}</td>
                              <td className="p-3 text-sm text-slate-500">
                                {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="p-3">
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                  Active
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Crown className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No premium subscribers yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary gap-2">
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Subscription Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Packages & Costs Tab */}
          <TabsContent value="packages">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle>Credit Packages</CardTitle>
                  <CardDescription>Edit package prices and credits</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {settings?.credit_packages && Object.entries(settings.credit_packages).map(([pkgId, pkg]) => (
                    <div key={pkgId} className="p-4 bg-slate-50 rounded-xl space-y-3">
                      <h4 className="font-medium capitalize">{pkg.name || pkgId}</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Credits</Label>
                          <Input
                            type="number"
                            value={pkg.credits}
                            onChange={(e) => updatePackage(pkgId, 'credits', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={pkg.price}
                            onChange={(e) => updatePackage(pkgId, 'price', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle>Credit Costs</CardTitle>
                  <CardDescription>Set costs for different actions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {settings?.credit_costs && (
                    <>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Book Analysis</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.book_analysis}
                          onChange={(e) => updateCost('book_analysis', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Test Generation (per 10 questions)</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.test_generation}
                          onChange={(e) => updateCost('test_generation', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Quiz Generation (per 10 questions)</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.quiz_generation}
                          onChange={(e) => updateCost('quiz_generation', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Quiz Results Download</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.download_quiz || 2}
                          onChange={(e) => updateCost('download_quiz', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Test Download</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.download_test}
                          onChange={(e) => updateCost('download_test', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Lecture Notes Generation</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.lecture_notes || 5}
                          onChange={(e) => updateCost('lecture_notes', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span>Lecture Notes Download</span>
                        <Input
                          type="number"
                          value={settings.credit_costs.download_notes || 2}
                          onChange={(e) => updateCost('download_notes', e.target.value)}
                          className="w-20 h-9"
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary gap-2">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </Button>
            </div>
          </TabsContent>

          {/* Payment Settings Tab */}
          <TabsContent value="payments">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle>Stripe Settings</CardTitle>
                  <CardDescription>Configure Stripe payment gateway</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mode</Label>
                    <Select
                      value={settings?.stripe_mode || "sandbox"}
                      onValueChange={(v) => setSettings(prev => ({ ...prev, stripe_mode: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Test)</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-3">Sandbox Keys</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">Publishable Key (pk_test_...)</Label>
                        <Input
                          value={settings?.stripe_sandbox_pk || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, stripe_sandbox_pk: e.target.value }))}
                          placeholder="pk_test_..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Secret Key (sk_test_...)</Label>
                        <Input
                          type="password"
                          value={settings?.stripe_sandbox_key || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, stripe_sandbox_key: e.target.value }))}
                          placeholder="sk_test_..."
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-3">Live Keys</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">Publishable Key (pk_live_...)</Label>
                        <Input
                          value={settings?.stripe_live_pk || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, stripe_live_pk: e.target.value }))}
                          placeholder="pk_live_..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Secret Key (sk_live_...)</Label>
                        <Input
                          type="password"
                          value={settings?.stripe_live_key || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, stripe_live_key: e.target.value }))}
                          placeholder="sk_live_..."
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle>PayPal Settings</CardTitle>
                  <CardDescription>Configure PayPal payment gateway</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mode</Label>
                    <Select
                      value={settings?.paypal_mode || "sandbox"}
                      onValueChange={(v) => setSettings(prev => ({ ...prev, paypal_mode: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Test)</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-3">Sandbox Credentials</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">Client ID</Label>
                        <Input
                          value={settings?.paypal_sandbox_client_id || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, paypal_sandbox_client_id: e.target.value }))}
                          placeholder="Client ID"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Secret Key</Label>
                        <Input
                          type="password"
                          value={settings?.paypal_sandbox_secret || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, paypal_sandbox_secret: e.target.value }))}
                          placeholder="Secret Key"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-3">Live Credentials</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">Client ID</Label>
                        <Input
                          value={settings?.paypal_live_client_id || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, paypal_live_client_id: e.target.value }))}
                          placeholder="Client ID"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Secret Key</Label>
                        <Input
                          type="password"
                          value={settings?.paypal_live_secret || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, paypal_live_secret: e.target.value }))}
                          placeholder="Secret Key"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary gap-2">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Payment Settings
              </Button>
            </div>
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="email">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#4F46E5]" />
                    SMTP Configuration
                  </CardTitle>
                  <CardDescription>Configure email sending for notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>SMTP Host</Label>
                      <Input
                        name={`smtp_host_${smtpFieldSuffix}`}
                        autoComplete="one-time-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        value={settings?.smtp_host || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                        placeholder="smtp.gmail.com"
                        className="mt-1"
                        data-testid="admin-smtp-host-input"
                      />
                    </div>
                    <div>
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        name={`smtp_port_${smtpFieldSuffix}`}
                        autoComplete="one-time-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        value={settings?.smtp_port ?? 587}
                        onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: e.target.value === "" ? "" : (parseInt(e.target.value) || 587) }))}
                        placeholder="587"
                        className="mt-1"
                        data-testid="admin-smtp-port-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Port 587 (recommended) or 25 with TLS. Port 465 uses SSL (TLS checkbox ignored).
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>SMTP Username</Label>
                    <Input
                      name={`smtp_user_${smtpFieldSuffix}`}
                      autoComplete="one-time-code"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      value={settings?.smtp_user || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                      placeholder="your-email@gmail.com"
                      className="mt-1"
                      data-testid="admin-smtp-user-input"
                    />
                  </div>
                  <div>
                    <Label>SMTP Password</Label>
                    <Input
                      type="text"
                      name={`smtp_pwd_${smtpFieldSuffix}`}
                      autoComplete="one-time-code"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      value={settings?.smtp_password || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                      placeholder="App password or SMTP password"
                      className="mt-1 font-mono"
                      style={{ WebkitTextSecurity: 'disc' }}
                      data-testid="admin-smtp-password-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Password is masked but not treated as a browser-managed password field (prevents autofill issues).
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Email</Label>
                      <Input
                        name={`smtp_from_email_${smtpFieldSuffix}`}
                        autoComplete="one-time-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        value={settings?.smtp_from_email || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                        placeholder="noreply@booktest.pro"
                        className="mt-1"
                        data-testid="admin-smtp-from-email-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Should match SMTP username domain or be authorized by your mail server.
                      </p>
                    </div>
                    <div>
                      <Label>From Name</Label>
                      <Input
                        name={`smtp_from_name_${smtpFieldSuffix}`}
                        autoComplete="one-time-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        value={settings?.smtp_from_name || "BookTest.pro"}
                        onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                        placeholder="BookTest.pro"
                        className="mt-1"
                        data-testid="admin-smtp-from-name-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="smtp_use_tls"
                      checked={settings?.smtp_use_tls !== false}
                      onChange={(e) => setSettings(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="smtp_use_tls">Use TLS (STARTTLS)</Label>
                    <span className="text-xs text-slate-500">For ports 587 or 25 only</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5 text-green-500" />
                    Admin Notifications
                  </CardTitle>
                  <CardDescription>Receive alerts for important events</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Admin Email</Label>
                    <Input
                      value={settings?.admin_notification_email || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, admin_notification_email: e.target.value }))}
                      placeholder="admin@booktest.pro"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Receive notifications for new users and payments</p>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-sm mb-3">Email Templates</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        Email Verification (new users)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        Welcome Email (after verification)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        Payment Success
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        Premium Activation
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        Admin: New User Notification
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        Admin: New Payment Notification
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowClearSmtpDialog(true)}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="admin-clear-smtp-btn"
              >
                <Trash2 className="w-4 h-4" />
                Clear SMTP Settings
              </Button>
              <Button 
                variant="outline"
                onClick={async () => {
                  const email = prompt("Enter email address to send test email:");
                  if (email) {
                    try {
                      await axios.post(`${API}/admin/test-email`, { to_email: email }, {
                        headers: getAuthHeaders(token)
                      });
                      toast.success("Test email sent!");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to send test email");
                    }
                  }
                }}
                className="gap-2"
                data-testid="admin-send-test-email-btn"
              >
                <Send className="w-4 h-4" />
                Send Test Email
              </Button>
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary gap-2" data-testid="admin-save-email-settings-btn">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Email Settings
              </Button>
            </div>
          </TabsContent>

          {/* LLM Usage Tab */}
          <TabsContent value="llm-usage">
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">LLM Usage & Cost Analytics</h3>
                <div className="flex items-center gap-2">
                  <Select value={llmUsagePeriod} onValueChange={handleLlmPeriodChange}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">Last 7 Days</SelectItem>
                      <SelectItem value="30d">Last 30 Days</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => fetchLlmUsage(llmUsagePeriod)}
                    disabled={loadingLlmUsage}
                  >
                    {loadingLlmUsage ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {llmUsage ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Total API Calls</p>
                            <p className="text-2xl font-bold text-blue-600">{llmUsage.summary?.total_calls || 0}</p>
                          </div>
                          <Brain className="w-8 h-8 text-blue-300" />
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {llmUsage.summary?.successful_calls || 0} successful
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">LLM Cost</p>
                            <p className="text-2xl font-bold text-red-600">${llmUsage.summary?.total_cost_usd?.toFixed(2) || '0.00'}</p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-red-300" />
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {llmUsage.summary?.total_tokens?.toLocaleString() || 0} tokens used
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Revenue</p>
                            <p className="text-2xl font-bold text-green-600">${llmUsage.summary?.total_revenue_usd?.toFixed(2) || '0.00'}</p>
                          </div>
                          <DollarSign className="w-8 h-8 text-green-300" />
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          From credit purchases
                        </p>
                      </CardContent>
                    </Card>

                    <Card className={`bg-gradient-to-br ${(llmUsage.summary?.profit_usd || 0) >= 0 ? 'from-emerald-50 border-emerald-100' : 'from-orange-50 border-orange-100'} to-white`}>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Net Profit</p>
                            <p className={`text-2xl font-bold ${(llmUsage.summary?.profit_usd || 0) >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                              ${llmUsage.summary?.profit_usd?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                          <BarChart3 className={`w-8 h-8 ${(llmUsage.summary?.profit_usd || 0) >= 0 ? 'text-emerald-300' : 'text-orange-300'}`} />
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {llmUsage.summary?.profit_margin_percent?.toFixed(1) || 0}% margin
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Usage by Action Type */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Cost by Action Type</CardTitle>
                      <CardDescription>Breakdown of LLM costs per feature</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(llmUsage.by_action_type || {}).map(([action, data]) => (
                          <div key={action} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                action === 'book_analysis' ? 'bg-blue-100 text-blue-600' :
                                action === 'test_generation' ? 'bg-purple-100 text-purple-600' :
                                action === 'quiz_generation' ? 'bg-green-100 text-green-600' :
                                action === 'lecture_notes' ? 'bg-orange-100 text-orange-600' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {action === 'book_analysis' ? <BookOpen className="w-5 h-5" /> :
                                 action === 'test_generation' ? <FileText className="w-5 h-5" /> :
                                 action === 'quiz_generation' ? <Brain className="w-5 h-5" /> :
                                 action === 'lecture_notes' ? <FileText className="w-5 h-5" /> :
                                 <Brain className="w-5 h-5" />}
                              </div>
                              <div>
                                <p className="font-medium capitalize">{action.replace(/_/g, ' ')}</p>
                                <p className="text-xs text-slate-500">{data.calls} calls • {data.tokens?.toLocaleString()} tokens</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">${data.cost?.toFixed(4)}</p>
                              <p className="text-xs text-slate-500">
                                ${(data.cost / (data.calls || 1)).toFixed(4)}/call
                              </p>
                            </div>
                          </div>
                        ))}
                        {Object.keys(llmUsage.by_action_type || {}).length === 0 && (
                          <p className="text-center text-slate-500 py-4">No usage data available</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Users by LLM Cost */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Top Users by LLM Cost</CardTitle>
                      <CardDescription>Users consuming the most LLM resources</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">User</th>
                              <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Calls</th>
                              <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Tokens</th>
                              <th className="text-right py-2 px-3 text-sm font-medium text-slate-500">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(llmUsage.top_users || []).map((u, idx) => (
                              <tr key={u.user_id} className="border-b hover:bg-slate-50">
                                <td className="py-3 px-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-400">#{idx + 1}</span>
                                    <div>
                                      <p className="font-medium text-sm">{u.name}</p>
                                      <p className="text-xs text-slate-500">{u.email}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-right py-3 px-3 text-sm">{u.calls}</td>
                                <td className="text-right py-3 px-3 text-sm">{u.tokens?.toLocaleString()}</td>
                                <td className="text-right py-3 px-3 text-sm font-medium text-red-600">${u.cost?.toFixed(4)}</td>
                              </tr>
                            ))}
                            {(llmUsage.top_users || []).length === 0 && (
                              <tr>
                                <td colSpan={4} className="text-center py-8 text-slate-500">No usage data available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Daily Trend */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Daily Cost Trend</CardTitle>
                      <CardDescription>LLM costs over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48 flex items-end gap-1">
                        {(llmUsage.daily_trend || []).map((day, idx) => {
                          const maxCost = Math.max(...(llmUsage.daily_trend || []).map(d => d.cost || 0), 0.01);
                          const height = ((day.cost || 0) / maxCost) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center">
                              <div 
                                className="w-full bg-gradient-to-t from-red-500 to-red-300 rounded-t transition-all hover:from-red-600 hover:to-red-400"
                                style={{ height: `${Math.max(height, 2)}%` }}
                                title={`${day.date}: $${day.cost?.toFixed(4)} (${day.calls} calls)`}
                              />
                              <span className="text-[10px] text-slate-400 mt-1 rotate-45 origin-left">{day.date}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-8 text-xs text-slate-500">
                        <span>Total: ${(llmUsage.daily_trend || []).reduce((sum, d) => sum + (d.cost || 0), 0).toFixed(4)}</span>
                        <span>Avg: ${((llmUsage.daily_trend || []).reduce((sum, d) => sum + (d.cost || 0), 0) / (llmUsage.daily_trend?.length || 1)).toFixed(4)}/day</span>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Brain className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No LLM usage data available yet</p>
                    <p className="text-sm text-slate-400 mt-1">Usage will be tracked when users generate tests, quizzes, or lecture notes</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* User Details Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Name</Label>
                  <p className="font-medium">{selectedUser.user.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{selectedUser.user.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Role</Label>
                  <p className="font-medium capitalize">{selectedUser.user.role}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Credits</Label>
                  <p className="font-medium">{selectedUser.user.credits}</p>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl">
                <Label className="text-sm font-medium mb-2 block">Adjust Credits</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreditAmount(prev => prev - 10)}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                    className="w-24 text-center"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreditAmount(prev => prev + 10)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleUpdateCredits(selectedUser.user.user_id)}
                    disabled={creditAmount === 0}
                    className="btn-primary"
                  >
                    Apply
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Stats</Label>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-lg font-bold">{selectedUser.stats.books}</p>
                    <p className="text-xs text-slate-500">Books</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-lg font-bold">{selectedUser.stats.tests}</p>
                    <p className="text-xs text-slate-500">Tests</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-lg font-bold">{selectedUser.stats.quizzes}</p>
                    <p className="text-xs text-slate-500">Quizzes</p>
                  </div>
                </div>
              </div>

              {selectedUser.payments.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Recent Payments</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedUser.payments.slice(0, 5).map((p) => (
                      <div key={p.purchase_id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                        <span>${p.amount} - {p.credits} credits</span>
                        <span className="text-slate-500">{p.payment_method}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteUser?.name} ({deleteUser?.email}) and all their data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear SMTP Settings Dialog */}
      <AlertDialog open={showClearSmtpDialog} onOpenChange={setShowClearSmtpDialog}>
        <AlertDialogContent data-testid="admin-clear-smtp-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear SMTP Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will wipe the current SMTP host, port, username, password, from-email and from-name from the database. Outgoing emails (verification, receipts, notifications) will stop working until you enter new SMTP credentials and save. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-clear-smtp-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearSmtp}
              disabled={clearingSmtp}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-clear-smtp-confirm-btn"
            >
              {clearingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, clear settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#4F46E5]" />
              Send Announcement / Newsletter
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="target_audience">Target Audience</Label>
              <Select 
                value={announcementData.target_audience}
                onValueChange={(value) => setAnnouncementData(prev => ({ ...prev, target_audience: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select audience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="teachers">Teachers Only</SelectItem>
                  <SelectItem value="students">Students Only</SelectItem>
                  <SelectItem value="parents">Parents Only</SelectItem>
                  <SelectItem value="premium">Premium Users Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="subject">Email Subject</Label>
              <Input 
                id="subject"
                placeholder="e.g., New Feature Announcement!"
                value={announcementData.subject}
                onChange={(e) => setAnnouncementData(prev => ({ ...prev, subject: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="title">Email Title (Header)</Label>
              <Input 
                id="title"
                placeholder="e.g., Exciting News!"
                value={announcementData.title}
                onChange={(e) => setAnnouncementData(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="content">Email Content (supports HTML)</Label>
              <textarea 
                id="content"
                placeholder="Write your announcement here... You can use HTML for formatting."
                value={announcementData.content}
                onChange={(e) => setAnnouncementData(prev => ({ ...prev, content: e.target.value }))}
                className="mt-1 w-full min-h-[150px] p-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Tip: Use &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt; tags for formatting.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendAnnouncement}
                disabled={sendingAnnouncement}
                className="bg-[#4F46E5] hover:bg-[#4338CA] gap-2"
              >
                {sendingAnnouncement ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Announcement
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Push Notification Dialog */}
      <Dialog open={showPushDialog} onOpenChange={setShowPushDialog}>
        <DialogContent className="max-w-2xl" data-testid="admin-push-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-600" />
              Send Push Notification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {pushStats && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-slate-700">
                  Total subscribed devices: <strong>{pushStats.total_subscriptions}</strong>
                </span>
                <span className={pushStats.configured ? "text-emerald-700" : "text-red-600"}>
                  {pushStats.configured ? "VAPID configured" : "VAPID NOT configured"}
                </span>
              </div>
            )}

            <div>
              <Label>Target Audience</Label>
              <Select
                value={pushData.target_audience}
                onValueChange={(value) => setPushData(prev => ({ ...prev, target_audience: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="admin-push-audience-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subscribed Users</SelectItem>
                  <SelectItem value="teachers">Teachers Only</SelectItem>
                  <SelectItem value="students">Students Only</SelectItem>
                  <SelectItem value="parents">Parents Only</SelectItem>
                  <SelectItem value="premium">Premium Users Only</SelectItem>
                  <SelectItem value="free">Free Users Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notification Title</Label>
              <Input
                placeholder="e.g., New feature is live!"
                value={pushData.title}
                onChange={(e) => setPushData(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1"
                maxLength={60}
                data-testid="admin-push-title-input"
              />
              <p className="text-xs text-slate-500 mt-1">{pushData.title.length}/60 characters</p>
            </div>

            <div>
              <Label>Message Body</Label>
              <textarea
                placeholder="Write a short, engaging message..."
                value={pushData.body}
                onChange={(e) => setPushData(prev => ({ ...prev, body: e.target.value }))}
                className="mt-1 w-full min-h-[90px] p-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                maxLength={200}
                data-testid="admin-push-body-input"
              />
              <p className="text-xs text-slate-500 mt-1">{pushData.body.length}/200 characters</p>
            </div>

            <div>
              <Label>Open URL on tap</Label>
              <Input
                placeholder="/dashboard"
                value={pushData.url}
                onChange={(e) => setPushData(prev => ({ ...prev, url: e.target.value }))}
                className="mt-1"
                data-testid="admin-push-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">Relative path (e.g., /pricing) or absolute URL.</p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPushDialog(false)} data-testid="admin-push-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSendPush}
                disabled={sendingPush || !pushStats?.configured}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="admin-push-send-btn"
              >
                {sendingPush ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    Send Push
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
